import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { updatePassword } from '../lib/auth';
import { useSession } from '../lib/auth';
import { validatePassword } from '../lib/passwordSecurity';

export function ResetPassword() {
  const nav = useNavigate();
  const { session, loading } = useSession();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // The reset link auto-signs the user in. If we don't have a session
  // when this page loads, the link is invalid or already used.
  useEffect(() => {
    if (loading) return;
    if (!session) {
      setError('This reset link has expired or already been used. Request a new one.');
    }
  }, [loading, session]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      // 12-char minimum + HIBP breach check. See lib/passwordSecurity.ts.
      const pwdError = await validatePassword(password);
      if (pwdError) {
        setError(pwdError);
        setSubmitting(false);
        return;
      }
      await updatePassword(password);
      setDone(true);
      // Briefly show success then route to home (user is already signed in)
      setTimeout(() => nav('/', { replace: true }), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update password');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthLayout title="Password updated">
        <h1>Password updated</h1>
        <p className="muted">Taking you to the dashboard…</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set a new password">
      <h1>Set a new password</h1>
      <p className="muted">
        At least 12 characters. A memorable passphrase like four random words
        is stronger than a short complex one.
      </p>

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="rp-pwd">New password</label>
          <input
            id="rp-pwd"
            type="password"
            autoComplete="new-password"
            enterKeyHint="next"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="rp-pwd2">Confirm new password</label>
          <input
            id="rp-pwd2"
            type="password"
            autoComplete="new-password"
            enterKeyHint="done"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={12}
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={submitting || !session}>
          {submitting ? 'Saving…' : 'Update password'}
        </button>
      </form>

      <p className="auth-footer">
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}
